import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface OrgUtilizationData {
  currentUtilization?: number;
  previousUtilization?: number;
  trend?: 'up' | 'down' | 'flat';
  topOverAllocated?: Array<{ teamName: string; utilization: number }>;
  topUnderAllocated?: Array<{ teamName: string; utilization: number }>;
}

export default function AnalyticsSnapshotWidget() {
  const [, navigate] = useLocation();

  const { data } = useQuery<any>({
    queryKey: ["/api/analytics/admin/organization-utilization"],
    select: (raw): OrgUtilizationData => {
      // Adapt whatever the existing endpoint returns
      const current = raw?.utilization ?? raw?.currentUtilization ?? 0;
      const previous = raw?.previousUtilization ?? 0;
      return {
        currentUtilization: Math.round(current),
        previousUtilization: Math.round(previous),
        trend: current > previous ? 'up' : current < previous ? 'down' : 'flat',
        topOverAllocated: (raw?.overAllocatedTeams ?? raw?.topOverAllocated ?? []).slice(0, 3),
        topUnderAllocated: (raw?.underAllocatedTeams ?? raw?.topUnderAllocated ?? []).slice(0, 3),
      };
    },
  });

  const utilization = data?.currentUtilization ?? 0;
  const getUtilColor = (val: number) => {
    if (val >= 80) return 'text-emerald-600 dark:text-emerald-400';
    if (val >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-rose-600 dark:text-rose-400';
  };

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold">Analytics</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/30 gap-1 font-semibold h-8 px-2"
            onClick={() => navigate("/analytics")}
          >
            Hub <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {!data ? (
          <div className="text-center py-6 text-muted-foreground">
            <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Loading analytics...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Main utilization gauge */}
            <div className="text-center p-4 rounded-xl bg-muted/50">
              <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Org Utilization</span>
              <div className={cn("text-4xl font-bold mt-1", getUtilColor(utilization))}>
                {utilization}%
              </div>
              {data.trend !== 'flat' && data.previousUtilization !== undefined && (
                <div className={cn(
                  "flex items-center justify-center gap-1 mt-1 text-xs",
                  data.trend === 'up' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                )}>
                  {data.trend === 'up' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                  <span>vs {data.previousUtilization}% last period</span>
                </div>
              )}
            </div>

            {/* Team highlights */}
            {(data.topOverAllocated?.length ?? 0) > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Over-Allocated</h4>
                {data.topOverAllocated!.map((t: { teamName: string; utilization: number }, i: number) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1">
                    <span className="text-foreground truncate">{t.teamName}</span>
                    <span className="text-rose-600 dark:text-rose-400 font-bold">{t.utilization}%</span>
                  </div>
                ))}
              </div>
            )}

            {(data.topUnderAllocated?.length ?? 0) > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Under-Allocated</h4>
                {data.topUnderAllocated!.map((t: { teamName: string; utilization: number }, i: number) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1">
                    <span className="text-foreground truncate">{t.teamName}</span>
                    <span className="text-amber-600 dark:text-amber-400 font-bold">{t.utilization}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
